import { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import { useConfig, useConfigMutate } from '@/hooks/useConfig';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const SCHEDULE_OPTIONS = [
  { value: 'sync', label: 'When new emails arrive', description: 'Generate after each sync with new mail' },
  { value: 'hourly', label: 'Every hour', description: 'Generate once per hour if new mail arrived' },
  { value: '3hrs', label: 'Every 3 hours', description: 'Generate every 3 hours if new mail arrived' },
  { value: 'daily', label: 'Once every day', description: 'Generate once per day if new mail arrived' },
];

const numberRanges = {
  digest_max_emails_to_scan: { min: 50, max: 5000 },
  analysis_min_body_length: { min: 0, max: 2000 },
  ai_cooldown_seconds: { min: 1, max: 60 },
  ai_max_requests_per_hour: { min: 1, max: 10000 },
};

export default function SettingsAutomation() {
  const { data: config, isLoading } = useConfig();
  const updateConfig = useConfigMutate();

  const [settings, setSettings] = useState({
    auto_analyze: false,
    auto_generate_report: false,
    auto_report_schedule: 'sync',
    auto_report_unread_only: false,
    auto_report_inbox_only: false,
    digest_max_emails_to_scan: 250,
    analysis_min_body_length: 120,
    ai_cooldown_seconds: 3,
    ai_max_requests_per_hour: 100,
  });

  useEffect(() => {
    if (config) {
      setSettings({
        auto_analyze: config.auto_analyze || false,
        auto_generate_report: config.auto_generate_report || false,
        auto_report_schedule: config.auto_report_schedule || 'sync',
        auto_report_unread_only: config.auto_report_unread_only || false,
        auto_report_inbox_only: config.auto_report_inbox_only || false,
        digest_max_emails_to_scan: config.digest_max_emails_to_scan || 250,
        analysis_min_body_length: config.analysis_min_body_length || 120,
        ai_cooldown_seconds: config.ai_cooldown_seconds || 3,
        ai_max_requests_per_hour: config.ai_max_requests_per_hour || 100,
      });
    }
  }, [config]);

  const autoSave = async (updates) => {
    try {
      await updateConfig.mutateAsync(updates);
    } catch (err) {
      console.error('Failed to auto-save automation settings:', err);
    }
  };

  const handleToggle = (key) => {
    const newValue = !settings[key];
    setSettings(prev => ({ ...prev, [key]: newValue }));
    autoSave({ [key]: newValue });
  };

  const handleScheduleChange = (value) => {
    setSettings(prev => ({ ...prev, auto_report_schedule: value }));
    autoSave({ auto_report_schedule: value });
  };

  const handleNumberChange = (key, value) => {
    const num = parseInt(value, 10);
    const range = numberRanges[key] || { min: 0, max: 9999 };
    if (isNaN(num) || num < range.min) return;
    const clamped = Math.min(num, range.max);
    setSettings(prev => ({ ...prev, [key]: clamped }));
    autoSave({ [key]: clamped });
  };

  const isAiConfigured = config?.ai_provider && config.ai_provider !== 'none';

  if (!isAiConfigured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" />Automation Settings</CardTitle>
          <CardDescription>Configure automatic email processing (settings save automatically)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
            <p className="text-sm text-amber-900 dark:text-amber-100">AI features must be enabled to use automation. Configure AI first.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" />Automation Settings</CardTitle>
        <CardDescription>Configure automatic email processing (settings save automatically)</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <LoadingSpinner /> : (
          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors">
              <div>
                <p className="text-sm font-medium">Auto-analyze new emails</p>
                <p className="text-xs text-muted-foreground">Analyze and categorize emails when new ones arrive</p>
              </div>
              <button type="button" onClick={() => handleToggle('auto_analyze')}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.auto_analyze ? 'bg-primary' : 'bg-input'}`}>
                <span className={`inline-block h-4 w-4 rounded-full bg-background transition-transform ${settings.auto_analyze ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
              </button>
            </label>

            {settings.auto_analyze && (
              <div className="ml-4 pl-4 border-l-2 border-muted space-y-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">AI Rate Limiting</p>
                <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors">
                  <div>
                    <p className="text-sm font-medium">Cooldown between requests</p>
                    <p className="text-xs text-muted-foreground">Seconds to wait between each AI API call (1-60)</p>
                  </div>
                  <input type="number" min="1" max="60"
                    value={settings.ai_cooldown_seconds}
                    onChange={e => handleNumberChange('ai_cooldown_seconds', e.target.value)}
                    className="w-20 h-8 text-sm text-right px-2 rounded-md border border-border bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                </label>
                <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors mt-1.5">
                  <div>
                    <p className="text-sm font-medium">Max requests per hour</p>
                    <p className="text-xs text-muted-foreground">Maximum AI API calls per hour (1-10000)</p>
                  </div>
                  <input type="number" min="1" max="10000"
                    value={settings.ai_max_requests_per_hour}
                    onChange={e => handleNumberChange('ai_max_requests_per_hour', e.target.value)}
                    className="w-20 h-8 text-sm text-right px-2 rounded-md border border-border bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                </label>
                <p className="text-xs font-medium text-muted-foreground mb-2 mt-4">Skip Trivial Emails</p>
                <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors">
                  <div>
                    <p className="text-sm font-medium">Min body length</p>
                    <p className="text-xs text-muted-foreground">Skip analysis if email body is shorter than this (0-2000). Emails with dates always pass through.</p>
                  </div>
                  <input type="number" min="0" max="2000"
                    value={settings.analysis_min_body_length}
                    onChange={e => handleNumberChange('analysis_min_body_length', e.target.value)}
                    className="w-20 h-8 text-sm text-right px-2 rounded-md border border-border bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                </label>
              </div>
            )}

            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors">
                <div>
                  <p className="text-sm font-medium">Auto-generate secretary report</p>
                  <p className="text-xs text-muted-foreground">Automatically generate secretary digests</p>
                </div>
                <button type="button" onClick={() => handleToggle('auto_generate_report')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.auto_generate_report ? 'bg-primary' : 'bg-input'}`}>
                  <span className={`inline-block h-4 w-4 rounded-full bg-background transition-transform ${settings.auto_generate_report ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
                </button>
              </label>

              {settings.auto_generate_report && (
                <div className="ml-4 pl-4 border-l-2 border-muted space-y-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Schedule</p>
                  <div className="grid gap-1.5">
                    {SCHEDULE_OPTIONS.map(opt => (
                      <div key={opt.value} onClick={() => handleScheduleChange(opt.value)}
                        className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${settings.auto_report_schedule === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{opt.label}</p>
                            <p className="text-xs text-muted-foreground">{opt.description}</p>
                          </div>
                          <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${settings.auto_report_schedule === opt.value ? 'border-primary' : 'border-muted'}`}>
                            {settings.auto_report_schedule === opt.value && <div className="h-2 w-2 rounded-full bg-primary" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-2">
              <p className="text-sm font-medium mb-2">Boswell Brief Filters</p>
              <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors">
                <div>
                  <p className="text-sm font-medium">Only include unread emails</p>
                  <p className="text-xs text-muted-foreground">Exclude emails marked as read by rules or manually</p>
                </div>
                <button type="button" onClick={() => handleToggle('auto_report_unread_only')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.auto_report_unread_only ? 'bg-primary' : 'bg-input'}`}>
                  <span className={`inline-block h-4 w-4 rounded-full bg-background transition-transform ${settings.auto_report_unread_only ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
                </button>
              </label>
              <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors mt-1.5">
                <div>
                  <p className="text-sm font-medium">Only include inbox</p>
                  <p className="text-xs text-muted-foreground">Ignore emails filtered into folders by rules</p>
                </div>
                <button type="button" onClick={() => handleToggle('auto_report_inbox_only')}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.auto_report_inbox_only ? 'bg-primary' : 'bg-input'}`}>
                  <span className={`inline-block h-4 w-4 rounded-full bg-background transition-transform ${settings.auto_report_inbox_only ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
                </button>
              </label>
              <label className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-colors mt-1.5">
                <div>
                  <p className="text-sm font-medium">Max emails to scan</p>
                  <p className="text-xs text-muted-foreground">Upper limit (50-5000) on how many recent emails to search through for the report</p>
                </div>
                <input type="number" min="50" max="5000"
                  value={settings.digest_max_emails_to_scan}
                  onChange={e => handleNumberChange('digest_max_emails_to_scan', e.target.value)}
                  className="w-20 h-8 text-sm text-right px-2 rounded-md border border-border bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
              </label>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
